import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { emptyFragment } from '@execonline-inc/execonline-ui';
import { Menu } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react';
import { decodeOnSelect, queryTaskAsPromise } from '../../../../Appy';
import { SearchParameter, SearchParameters } from '../../../../ListFilter/Types';
import { resourceDecoder } from '../../../../Resource/Decoders';
import { Resource } from '../../../../Resource/Types';
import Label from '../../../Form/Label';
import { numberOptionsDecoder } from '../../../Options/Decoders';
import { NumberOptions } from '../../../Options/Types';
import { LearningCollectionSummariesResource } from '../../Types';
import Dropdown from './Dropdown';
import MenuButton from './MenuButton';
import OwnerFilterSkeleton from './OwnerFilterSkeleton';

type OwnerOptions = NumberOptions;
export type OwnerOptionsResource = Resource<OwnerOptions>;
const ownerOptionsResourceDecoder = resourceDecoder(numberOptionsDecoder);

interface Props {
  updatingSearchParameters: (searchParameter: SearchParameter) => void;
  learningCollectionSummariesResource: LearningCollectionSummariesResource;
  searchParameters: SearchParameters;
}

function OwnerFilter({
  updatingSearchParameters,
  learningCollectionSummariesResource,
  searchParameters,
}: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['owner-options'],
    enabled: !!learningCollectionSummariesResource,
    retry: 3,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
    queryFn: () => queryTaskAsPromise('options', learningCollectionSummariesResource.links),
    select: decodeOnSelect(ownerOptionsResourceDecoder),
  });

  if (isLoading) {
    return <OwnerFilterSkeleton />;
  }

  if (error || !data) {
    warnAndNotify('owner-options-load-failure', 'Failed to load owner options:', error || {});
    return emptyFragment();
  }

  return (
    <div className="flex md:flex-row z-40 mr-3 ml-0 w-full md:ml-4">
      <div className="mr-3" data-intercom-target="owner-filter">
        <Label labelText="Owner" className="text-xs font-semibold text-gray-800" />
        <Menu as="div">
          <MenuButton searchParameters={searchParameters} />
          <Dropdown
            searchParameters={searchParameters}
            updatingSearchParameters={updatingSearchParameters}
            ownerOptionsData={data}
          />
        </Menu>
      </div>
    </div>
  );
}

export default observer(OwnerFilter);
